import { UploadApiError } from "@/lib/upload/upload-api";

const MAX_RETRIES = 5;

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function normalizeEtag(etag: string) {
  const trimmed = etag.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed;
  return `"${trimmed.replaceAll('"', "")}"`;
}

type UploadProgressHandler = (loaded: number, total: number) => void;

function putOnce(
  url: string,
  body: Blob,
  signal: AbortSignal | undefined,
  onProgress: UploadProgressHandler | undefined,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.responseType = "text";

    const onAbort = () => {
      xhr.abort();
      reject(new DOMException("Upload aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded, event.total);
      }
    });

    xhr.addEventListener("load", () => {
      signal?.removeEventListener("abort", onAbort);
      if (xhr.status === 403) {
        reject(new UploadApiError("Upload URL expired", 403));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new UploadApiError(`Upload failed (${xhr.status})`, xhr.status));
        return;
      }
      const etag = xhr.getResponseHeader("ETag");
      if (!etag) {
        reject(new UploadApiError("Missing ETag from upload response", xhr.status));
        return;
      }
      resolve(normalizeEtag(etag));
    });

    xhr.addEventListener("error", () => {
      signal?.removeEventListener("abort", onAbort);
      reject(new UploadApiError("Upload failed (network error)", 0));
    });

    xhr.addEventListener("abort", () => {
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Upload aborted", "AbortError"));
    });

    xhr.send(body);
  });
}

export async function putWithRetry(
  url: string,
  body: Blob,
  signal?: AbortSignal,
  onProgress?: UploadProgressHandler,
) {
  let attempt = 0;
  while (true) {
    try {
      return await putOnce(url, body, signal, onProgress);
    } catch (error) {
      attempt += 1;
      if (attempt >= MAX_RETRIES || (error instanceof UploadApiError && error.status === 403)) {
        throw error;
      }
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      await sleep(250 * 2 ** attempt);
    }
  }
}
